// The widget `help` field — an OPTIONAL (?) beside a widget's header title.
//
// ── WHY ───────────────────────────────────────────────────────────────────
// A widget header holds a title and, optionally, one short subtitle. A module
// that wants to EXPLAIN its widget has nowhere to put the sentence, so it puts
// it in the header: VaultOS 1.38.1 shipped five home widgets whose captions ran
// as long as their titles, and the board read as prose instead of as cards
// (measured 2026-09-10). `help` is that sentence's proper home — folded behind
// a question mark the reader opens when they want it, and closed the rest of
// the time, so the header keeps ONE line of chrome at every width.
//
// ── THE CONTRACT (what a module author writes) ────────────────────────────
// On a manifest widget row (`manifest.widgets[]`), and on a house catalog row
// in pages/main.js, `help` is optional and takes either shape:
//
//   help: 'vault.widget.rooms.help'                       // shorthand — tip only
//   help: { tip: 'vault.widget.rooms.help' }              // same thing
//   help: { tip: '…', ariaLabel: 'vault.widget.rooms.helpAria' }
//
//   tip        REQUIRED. A string OR a polyglot key. What the (?) reveals.
//              Resolved EXACTLY the way a widget `title` is — through
//              `el.call('polyglot', …)` at the render site — and polyglot
//              returns a non-key verbatim, so a plain English sentence and a
//              bundle key both work, with no flag to choose between them.
//   ariaLabel  OPTIONAL. A string OR a polyglot key. The accessible name of
//              the (?) control. DEFAULTS TO `tip`: the control's only purpose
//              is to reveal that sentence, so naming it with the sentence
//              loses nothing and costs no bundle key. Pass a short one when
//              the tip is long.
//
// Anything else — no `help`, no `tip`, a blank string, an array, a number, a
// function — normalises to `null`, and the widget header renders NOTHING
// extra. A `help` is never a required field and its absence is never an error.
//
// ── WHY IT IS NORMALISED ONCE, HERE ───────────────────────────────────────
// Three readers want it (the module-manifest loader in
// functions/workspaceProjects.js, the static widget header and the module
// widget header in pages/main.js), and a field with two accepted shapes read
// three times is a field with three slightly different meanings. One
// normaliser, one output shape: `{ tip, ariaLabel }` with both strings
// non-empty, or `null`.
export const normalizeWidgetHelp = (help) => {
  if (!help) return null
  if (typeof help === 'string') {
    const tip = help.trim()
    return tip ? { tip, ariaLabel: tip } : null
  }
  if (typeof help !== 'object' || Array.isArray(help)) return null
  const tip = typeof help.tip === 'string' ? help.tip.trim() : ''
  if (!tip) return null
  const aria = typeof help.ariaLabel === 'string' ? help.ariaLabel.trim() : ''
  return { tip, ariaLabel: aria || tip }
}
